import "overlayscrollbars/css/OverlayScrollbars.css";

import classnames from "classnames";
import React, { useEffect, useRef } from "react";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

import { Check } from "./icons/Check";
import { T } from "./DropdownMenu.tlocale";
import { useExternalInternalString } from "../utils/useExternalInternal";

export interface DropdownMenuItem {
  label: string | JSX.Element;
  value: string;
}

interface DropdownMenuProps {
  maxHeight?: number; // in number of items
  items: DropdownMenuItem[];
  active?: string;
  onClick?: (value: string) => void;
  hover?: string;
  onHover?: (value: string | undefined) => void;
  rounded?: boolean;
  align?: "left" | "right";
}

export const DropdownMenu = React.forwardRef<HTMLDivElement, DropdownMenuProps>((props, ref) => {
  const itemsRef = useRef<HTMLDivElement | null>(null);
  const isRight = props.align === "right";
  const { onClick, maxHeight } = props;
  const { value: hover, onChange: onHover } = useExternalInternalString(props.hover, props.onHover);
  useEffect(() => {
    const parentEl = itemsRef.current;
    if (!parentEl) return;
    const p = parentEl.parentElement?.parentElement;
    if (!p) return;
    const c = parentEl.children;
    const n = c.length;
    for (let i = 0; i < n; i++) {
      const el = c[i] as HTMLElement;
      // that's a bit hacky, but it works, we could leave an explicit data- arg, but whatever
      if (el.className.indexOf("bg-input") !== -1) {
        const y = el.offsetTop;
        const h = el.clientHeight;
        if (y < p.scrollTop) {
          p.scrollTop = y;
        } else if (y + h > p.scrollTop + p.clientHeight) {
          p.scrollTop = y + h - p.clientHeight;
        }
      }
    }
  }, [hover]);
  return (
    <div className={props.rounded ? "bg-white rounded-xl border border-line overflow-hidden" : undefined} ref={ref}>
      <OverlayScrollbarsComponent>
        <div
          ref={itemsRef}
          className="txt-body-14 text-ash flex flex-col"
          style={maxHeight !== undefined ? { maxHeight: maxHeight * 34 } : undefined}
        >
          {props.items.map((item) => {
            const { value, label } = item;
            const isActive = !!value && value === props.active;
            const isHover = hover !== undefined && value === hover;
            const className = classnames(
              "relative select-none cursor-pointer w-full h-[34px] flex-shrink-0 flex items-center",
              {
                "bg-input": isHover,
                "pl-4 pr-12": isActive && !isRight,
                "pl-4 pr-4": !isActive && !isRight,
                "pr-4 pl-12": isActive && isRight,
                "pr-4 pl-4": !isActive && isRight,
                "justify-start": !isRight,
                "justify-end": isRight,
              }
            );
            const checkClassName = isRight ? "absolute left-4 w-4 h-4" : "absolute right-4 w-4 h-4";
            return (
              <div
                key={value}
                className={className}
                onMouseEnter={() => {
                  onHover(value);
                }}
                onMouseLeave={() => {
                  onHover(undefined);
                }}
                onClick={() => {
                  onClick?.(value);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
              >
                {label || <i>{T("Clear")}</i>}
                {isActive ? <Check className={checkClassName} /> : null}
              </div>
            );
          })}
        </div>
      </OverlayScrollbarsComponent>
    </div>
  );
});
